import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Plus, Pencil, Trash2, Image as ImageIcon, Video, ChevronDown, ChevronUp, GripVertical, FolderOpen } from 'lucide-react';
import { getAllProjectsAdmin, deleteProject, createProject, updateProject, uploadMedia, getDeletedProjects, restoreProject, permanentDeleteProject, reorderProjects } from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { RotateCcw, Trash } from 'lucide-react';
import CloudinaryLibrary from '@/components/CloudinaryLibrary';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Generar ID único
const generateId = () => Math.random().toString(36).substring(2, 15);

// Componente de fila arrastrable
const SortableProjectRow = ({ project, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.slug });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 rounded-xl border bg-white p-4 shadow-sm ${isDragging ? 'border-blue-500 shadow-lg' : 'border-slate-200'}`}
      data-testid={`project-row-${project.slug}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-2 hover:bg-slate-100 rounded-lg"
        data-testid={`drag-handle-${project.slug}`}
      >
        <GripVertical size={20} className="text-slate-400" />
      </div>
      
      <div className="h-20 w-32 overflow-hidden rounded-lg bg-slate-100 flex-shrink-0">
        {project.media?.images?.[0]?.url && (
          <img src={project.media.images[0].url} alt={project.nombre} className="h-full w-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-slate-900 truncate">{project.nombre}</h3>
        <p className="text-sm text-slate-600 truncate">{project.ubicacion} • {project.estadoCustom || project.estado}</p>
        <div className="mt-1 flex gap-2 text-xs text-slate-500">
          {project.naves?.length > 0 && <span>{project.naves.length} naves</span>}
          {project.stats?.length > 0 && <span>• {project.stats.length} stats</span>}
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <Button variant="outline" size="sm" onClick={() => onEdit(project)} data-testid={`edit-project-${project.slug}`}>
          <Pencil size={16} />
        </Button>
        <Button variant="outline" size="sm" onClick={() => onDelete(project.slug)} data-testid={`delete-project-${project.slug}`}>
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );
};

export const AdminProjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [deletedProjects, setDeletedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeView, setActiveView] = useState('active');
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!user) {
      navigate('/admin');
    } else {
      loadProjects();
    }
  }, [user, navigate]);

  const loadProjects = async () => {
    try {
      const [activeData, deletedData] = await Promise.all([
        getAllProjectsAdmin(),
        getDeletedProjects()
      ]);
      setProjects(activeData);
      setDeletedProjects(deletedData);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = projects.findIndex((p) => p.slug === active.id);
      const newIndex = projects.findIndex((p) => p.slug === over.id);
      
      const newProjects = arrayMove(projects, oldIndex, newIndex);
      setProjects(newProjects);
      
      // Save new order to backend
      setIsSavingOrder(true);
      try {
        const orderData = newProjects.map((p, index) => ({
          slug: p.slug,
          orden: index
        }));
        await reorderProjects(orderData);
        toast.success('Orden guardado');
      } catch (error) {
        console.error('Failed to save order:', error);
        toast.error('Error al guardar el orden');
        // Revert on error
        loadProjects();
      } finally {
        setIsSavingOrder(false);
      }
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setIsDialogOpen(true);
  };

  const handleDelete = async (slug) => {
    if (!confirm('¿Estás seguro de eliminar este proyecto? Se moverá a la papelera.')) return;
    try {
      await deleteProject(slug);
      toast.success('Proyecto movido a papelera');
      loadProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Error al eliminar proyecto');
    }
  };

  const handleRestore = async (slug) => {
    try {
      await restoreProject(slug);
      toast.success('Proyecto restaurado');
      loadProjects();
    } catch (error) {
      console.error('Failed to restore project:', error);
      toast.error('Error al restaurar proyecto');
    }
  };

  const handlePermanentDelete = async (slug) => {
    if (!confirm('⚠️ ATENCIÓN: Esta acción es IRREVERSIBLE. ¿Eliminar permanentemente este proyecto?')) return;
    try {
      await permanentDeleteProject(slug);
      toast.success('Proyecto eliminado permanentemente');
      loadProjects();
    } catch (error) {
      console.error('Failed to permanently delete project:', error);
      toast.error('Error al eliminar proyecto');
    }
  };

  const handleNewProject = () => {
    setEditingProject({
      slug: '',
      nombre: '',
      ubicacion: '',
      estado: 'En planificación',
      superficieM2: '',
      descripcionCorta: '',
      descripcionLarga: '',
      features: [],
      media: { images: [], videos: [] },
      mapEmbedUrl: '',
      orden: projects.length,
      // Nuevos campos de landing
      hero: { videoUrl: '', tag: 'Proyecto Destacado', titulo: '', subtitulo: '' },
      stats: [],
      naves: [],
      areasComunes: [],
      areasComunesVideo: '',
      ubicacionInfo: { direccion: '', accesos: '', transporte: '' },
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <header className="border-b border-slate-200 bg-white">
        <div className="container-safe flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-lg font-semibold text-slate-900">Gestionar Proyectos</h1>
          </div>
          <Button onClick={handleNewProject} data-testid="new-project-btn">
            <Plus size={16} className="mr-2" /> Nuevo Proyecto
          </Button>
        </div>
      </header>

      <main className="container-safe py-8">
        {loading ? (
          <div className="text-center text-slate-600">Cargando proyectos...</div>
        ) : (
          <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="active" className="flex items-center gap-2">
                Proyectos Activos
                {projects.length > 0 && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs">{projects.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="trash" className="flex items-center gap-2">
                <Trash size={14} />
                Papelera
                {deletedProjects.length > 0 && <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs">{deletedProjects.length}</span>}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {isSavingOrder && (
                <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700 flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  Guardando orden...
                </div>
              )}
              <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
                <GripVertical className="inline-block w-4 h-4 mr-1 text-slate-400" />
                <strong>Tip:</strong> Arrastrá los proyectos usando el ícono <GripVertical className="inline-block w-4 h-4 mx-1" /> para reordenarlos. El orden se guarda automáticamente.
              </div>
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={projects.map(p => p.slug)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="grid gap-4">
                    {projects.map((project) => (
                      <SortableProjectRow
                        key={project.slug}
                        project={project}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {projects.length === 0 && (
                <div className="py-12 text-center text-slate-600">
                  No hay proyectos activos. Hacé clic en "Nuevo Proyecto" para crear uno.
                </div>
              )}
            </TabsContent>

            <TabsContent value="trash">
              <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                <strong>Papelera:</strong> Los proyectos eliminados se mantienen aquí. Podés restaurarlos o eliminarlos permanentemente.
              </div>
              <div className="grid gap-4">
                {deletedProjects.map((project) => (
                  <div
                    key={project.slug}
                    className="flex items-center gap-4 rounded-xl border border-red-200 bg-red-50/50 p-4 shadow-sm"
                    data-testid={`trash-project-row-${project.slug}`}
                  >
                    <div className="h-20 w-32 overflow-hidden rounded-lg bg-slate-100 opacity-60">
                      {project.media?.images?.[0]?.url && (
                        <img src={project.media.images[0].url} alt={project.nombre} className="h-full w-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 opacity-80">
                      <h3 className="font-semibold text-slate-900">{project.nombre}</h3>
                      <p className="text-sm text-slate-600">{project.ubicacion} • {project.estadoCustom || project.estado}</p>
                      <span className="mt-1 inline-block text-xs text-red-600">En papelera</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleRestore(project.slug)} className="text-green-700 border-green-300 hover:bg-green-50" data-testid={`restore-project-${project.slug}`}>
                        <RotateCcw size={16} className="mr-1" /> Restaurar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handlePermanentDelete(project.slug)} className="text-red-700 border-red-300 hover:bg-red-50" data-testid={`permanent-delete-${project.slug}`}>
                        <Trash2 size={16} className="mr-1" /> Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
                {deletedProjects.length === 0 && (
                  <div className="py-12 text-center text-slate-500">
                    La papelera está vacía.
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      <ProjectEditDialog
        project={editingProject}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSave={loadProjects}
      />
    </div>
  );
};

// ============== COMPONENTE DE EDICIÓN ==============
const ProjectEditDialog = ({ project, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState(project);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (project) {
      setFormData({
        ...project,
        hero: project.hero || { videoUrl: '', tag: 'Proyecto Destacado', titulo: '', subtitulo: '' },
        stats: project.stats || [],
        naves: project.naves || [],
        areasComunes: project.areasComunes || [],
        areasComunesVideo: project.areasComunesVideo || '',
        ubicacionInfo: project.ubicacionInfo || { direccion: '', accesos: '', transporte: '' },
      });
    }
  }, [project]);

  const handleSave = async () => {
    if (!formData.slug || !formData.nombre) {
      toast.error('Completá los campos obligatorios');
      return;
    }
    setSaving(true);
    try {
      const isNew = !project?.slug || project.slug !== formData.slug;
      if (isNew) {
        await createProject(formData);
        toast.success('Proyecto creado');
      } else {
        await updateProject(formData.slug, formData);
        toast.success('Proyecto actualizado');
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!formData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[95vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{project?.slug ? 'Editar Proyecto' : 'Nuevo Proyecto'}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="hero">Hero</TabsTrigger>
            <TabsTrigger value="stats">Stats</TabsTrigger>
            <TabsTrigger value="naves">Naves</TabsTrigger>
            <TabsTrigger value="areas">Áreas</TabsTrigger>
            <TabsTrigger value="ubicacion">Ubicación</TabsTrigger>
          </TabsList>

          {/* TAB: GENERAL */}
          <TabsContent value="general" className="space-y-4 pt-4">
            <GeneralTab formData={formData} setFormData={setFormData} isNew={!project?.slug} />
          </TabsContent>

          {/* TAB: HERO */}
          <TabsContent value="hero" className="space-y-4 pt-4">
            <HeroTab formData={formData} setFormData={setFormData} />
          </TabsContent>

          {/* TAB: STATS */}
          <TabsContent value="stats" className="space-y-4 pt-4">
            <StatsTab formData={formData} setFormData={setFormData} />
          </TabsContent>

          {/* TAB: NAVES */}
          <TabsContent value="naves" className="space-y-4 pt-4">
            <NavesTab formData={formData} setFormData={setFormData} />
          </TabsContent>

          {/* TAB: ÁREAS COMUNES */}
          <TabsContent value="areas" className="space-y-4 pt-4">
            <AreasTab formData={formData} setFormData={setFormData} />
          </TabsContent>

          {/* TAB: UBICACIÓN */}
          <TabsContent value="ubicacion" className="space-y-4 pt-4">
            <UbicacionTab formData={formData} setFormData={setFormData} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-project-btn">
            {saving ? 'Guardando...' : 'Guardar Proyecto'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============== TAB: GENERAL ==============
const GeneralTab = ({ formData, setFormData, isNew }) => {
  const [uploading, setUploading] = useState(false);
  const [showCloudinaryLibrary, setShowCloudinaryLibrary] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    try {
      const results = await Promise.all(acceptedFiles.map(f => uploadMedia(f)));
      const newImages = results.map((r, idx) => ({
        url: r.url,
        alt: acceptedFiles[idx].name,
        orden: (formData?.media?.images?.length || 0) + idx,
      }));
      setFormData(prev => ({
        ...prev,
        media: { ...prev.media, images: [...(prev.media?.images || []), ...newImages] },
      }));
      toast.success(`${results.length} imagen(es) subida(s)`);
    } catch (error) {
      toast.error('Error al subir imágenes');
    } finally {
      setUploading(false);
    }
  }, [formData, setFormData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'image/*': [] }, multiple: true });

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      media: { ...prev.media, images: prev.media.images.filter((_, i) => i !== index) },
    }));
  };

  const handleCloudinarySelect = (items) => {
    const selectedItems = Array.isArray(items) ? items : [items];
    const newImages = selectedItems.map((item, idx) => ({
      url: item.url,
      alt: item.filename || 'Imagen de Cloudinary',
      orden: (formData?.media?.images?.length || 0) + idx,
    }));
    setFormData(prev => ({
      ...prev,
      media: { ...prev.media, images: [...(prev.media?.images || []), ...newImages] },
    }));
    toast.success(`${selectedItems.length} imagen(es) agregada(s) desde Cloudinary`);
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Slug (URL) *</Label>
          <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="proyecto-ejemplo" disabled={!isNew} />
        </div>
        <div>
          <Label>Nombre *</Label>
          <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Nombre del proyecto" />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Ubicación *</Label>
          <Input value={formData.ubicacion} onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })} placeholder="Buenos Aires, Argentina" />
        </div>
        <div>
          <Label>Estado</Label>
          <Select value={formData.estado} onValueChange={(v) => setFormData({ ...formData, estado: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Desarrollado">Desarrollado</SelectItem>
              <SelectItem value="En obra">En obra</SelectItem>
              <SelectItem value="En planificación">En planificación</SelectItem>
              <SelectItem value="Personalizado">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {formData.estado === 'Personalizado' && (
        <div>
          <Label>Estado Personalizado</Label>
          <Input 
            value={formData.estadoCustom || ''} 
            onChange={(e) => setFormData({ ...formData, estadoCustom: e.target.value })} 
            placeholder="Ej: En Remodelación, Próximamente, etc." 
          />
        </div>
      )}
      <div>
        <Label>Superficie Total (m²)</Label>
        <Input type="number" value={formData.superficieM2 || ''} onChange={(e) => setFormData({ ...formData, superficieM2: parseInt(e.target.value) || null })} placeholder="12000" />
      </div>
      <div>
        <Label>Descripción Corta *</Label>
        <Textarea value={formData.descripcionCorta} onChange={(e) => setFormData({ ...formData, descripcionCorta: e.target.value })} rows={2} />
      </div>
      <div>
        <Label>Descripción Larga *</Label>
        <Textarea value={formData.descripcionLarga} onChange={(e) => setFormData({ ...formData, descripcionLarga: e.target.value })} rows={4} />
      </div>

      {/* Features */}
      <div>
        <Label>Características</Label>
        <div className="mt-2 space-y-2">
          {formData.features?.map((f, idx) => (
            <div key={idx} className="flex gap-2">
              <Input value={f} onChange={(e) => {
                const newFeatures = [...formData.features];
                newFeatures[idx] = e.target.value;
                setFormData({ ...formData, features: newFeatures });
              }} />
              <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, features: formData.features.filter((_, i) => i !== idx) })}>
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, features: [...(formData.features || []), ''] })}>
            <Plus size={16} className="mr-2" /> Agregar característica
          </Button>
        </div>
      </div>

      {/* Imágenes Generales */}
      <div>
        <Label>Imágenes Generales</Label>
        <div className="mt-2 flex gap-2">
          <div {...getRootProps()} className={`flex-1 rounded-lg border-2 border-dashed p-6 text-center cursor-pointer ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300'}`}>
            <input {...getInputProps()} />
            <ImageIcon className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-2 text-sm text-slate-600">{uploading ? 'Subiendo...' : 'Arrastrá imágenes o hacé clic'}</p>
          </div>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => setShowCloudinaryLibrary(true)}
            className="flex flex-col items-center justify-center h-auto min-h-[120px] px-4 border-2 border-dashed hover:border-blue-500 hover:bg-blue-50"
            data-testid="open-cloudinary-library-general"
          >
            <FolderOpen className="h-8 w-8 text-slate-400 mb-2" />
            <span className="text-sm text-slate-600">Cloudinary</span>
          </Button>
        </div>
        {formData.media?.images?.length > 0 && (
          <div className="mt-4 grid gap-2 grid-cols-4">
            {formData.media.images.map((img, idx) => (
              <div key={idx} className="relative group">
                <img src={img.url} alt={img.alt} className="aspect-video w-full rounded-lg object-cover" />
                <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 rounded-full bg-red-500 p-1 text-white opacity-0 group-hover:opacity-100">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cloudinary Library Modal */}
      <CloudinaryLibrary 
        open={showCloudinaryLibrary}
        onClose={() => setShowCloudinaryLibrary(false)}
        onSelect={handleCloudinarySelect}
        multiple={true}
        resourceType="image"
      />
    </div>
  );
};

// ============== TAB: HERO ==============
const HeroTab = ({ formData, setFormData }) => {
  const [uploading, setUploading] = useState(false);
  const [showCloudinaryLibrary, setShowCloudinaryLibrary] = useState(false);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadMedia(file);
      setFormData(prev => ({ ...prev, hero: { ...prev.hero, videoUrl: result.url } }));
      toast.success('Video subido');
    } catch (error) {
      toast.error('Error al subir video');
    } finally {
      setUploading(false);
    }
  };

  const handleCloudinarySelect = (item) => {
    setFormData(prev => ({ ...prev, hero: { ...prev.hero, videoUrl: item.url } }));
    toast.success('Video seleccionado de Cloudinary');
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
        <strong>Hero Section:</strong> Esta es la primera sección que ve el usuario al entrar al proyecto. Incluye un video de fondo, badge, título y subtítulo.
      </div>

      <div>
        <Label>Video de Fondo del Hero</Label>
        <div className="mt-2 space-y-2">
          <Input value={formData.hero?.videoUrl || ''} onChange={(e) => setFormData({ ...formData, hero: { ...formData.hero, videoUrl: e.target.value } })} placeholder="URL del video o subí uno" />
          <div className="flex gap-2">
            <input type="file" accept="video/mp4,video/webm" onChange={handleVideoUpload} className="flex-1 block text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100" disabled={uploading} />
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowCloudinaryLibrary(true)}
              className="whitespace-nowrap"
              data-testid="open-cloudinary-library-hero"
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              Cloudinary
            </Button>
          </div>
          {uploading && <p className="text-xs text-slate-600">Subiendo video...</p>}
          {formData.hero?.videoUrl && (
            <video src={formData.hero.videoUrl} className="w-full max-w-md rounded-lg mt-2" controls muted />
          )}
        </div>
      </div>

      <div>
        <Label>Badge / Tag</Label>
        <Input value={formData.hero?.tag || ''} onChange={(e) => setFormData({ ...formData, hero: { ...formData.hero, tag: e.target.value } })} placeholder="Proyecto Destacado" />
      </div>

      <div>
        <Label>Título del Hero</Label>
        <Input value={formData.hero?.titulo || ''} onChange={(e) => setFormData({ ...formData, hero: { ...formData.hero, titulo: e.target.value } })} placeholder="Dejá vacío para usar el nombre del proyecto" />
        <p className="text-xs text-slate-500 mt-1">Si está vacío, se usará el nombre del proyecto</p>
      </div>

      <div>
        <Label>Subtítulo del Hero</Label>
        <Textarea value={formData.hero?.subtitulo || ''} onChange={(e) => setFormData({ ...formData, hero: { ...formData.hero, subtitulo: e.target.value } })} rows={2} placeholder="Dejá vacío para usar la descripción corta" />
      </div>

      {/* Cloudinary Library Modal for Videos */}
      <CloudinaryLibrary 
        open={showCloudinaryLibrary}
        onClose={() => setShowCloudinaryLibrary(false)}
        onSelect={handleCloudinarySelect}
        multiple={false}
        resourceType="video"
      />
    </div>
  );
};

// ============== TAB: STATS ==============
const StatsTab = ({ formData, setFormData }) => {
  const addStat = () => {
    setFormData(prev => ({ ...prev, stats: [...(prev.stats || []), { value: '', label: '' }] }));
  };

  const updateStat = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      stats: prev.stats.map((s, i) => i === index ? { ...s, [field]: value } : s),
    }));
  };

  const removeStat = (index) => {
    setFormData(prev => ({ ...prev, stats: prev.stats.filter((_, i) => i !== index) }));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
        <strong>Estadísticas:</strong> Números destacados que aparecen debajo de la descripción (ej: "5 Naves", "8.500 m²", "24/7 Seguridad").
      </div>

      <div className="space-y-3">
        {formData.stats?.map((stat, idx) => (
          <div key={idx} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Valor</Label>
              <Input value={stat.value} onChange={(e) => updateStat(idx, 'value', e.target.value)} placeholder="5" />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Etiqueta</Label>
              <Input value={stat.label} onChange={(e) => updateStat(idx, 'label', e.target.value)} placeholder="Naves industriales" />
            </div>
            <Button variant="outline" size="sm" onClick={() => removeStat(idx)}>
              <Trash2 size={16} />
            </Button>
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={addStat}>
        <Plus size={16} className="mr-2" /> Agregar Estadística
      </Button>
    </div>
  );
};

// ============== TAB: NAVES ==============
const NavesTab = ({ formData, setFormData }) => {
  const [expandedNave, setExpandedNave] = useState(null);
  const [uploadingNaveImg, setUploadingNaveImg] = useState(null);
  const [cloudinaryModal, setCloudinaryModal] = useState({ open: false, naveId: null, type: 'image' });

  const addNave = () => {
    const newNave = {
      id: generateId(),
      nombre: `Nave ${(formData.naves?.length || 0) + 1}`,
      imagen: '',
      estado: 'Disponible',
      estadoCustom: '',
      superficie: '',
      altura: '',
      descripcion: '',
      techo: '',
      instalaciones: '',
      accesos: '',
      galeria: [],
      video: '',
    };
    setFormData(prev => ({ ...prev, naves: [...(prev.naves || []), newNave] }));
    setExpandedNave(newNave.id);
  };

  const updateNave = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      naves: prev.naves.map(n => n.id === id ? { ...n, [field]: value } : n),
    }));
  };

  const handleCloudinarySelect = (items) => {
    const { naveId, type } = cloudinaryModal;
    if (!naveId) return;

    if (type === 'image') {
      // Single image for nave principal
      updateNave(naveId, 'imagen', items.url);
      toast.success('Imagen de nave seleccionada');
    } else if (type === 'gallery') {
      // Multiple images for gallery
      const selectedItems = Array.isArray(items) ? items : [items];
      const nave = formData.naves.find(n => n.id === naveId);
      const newGallery = [...(nave?.galeria || []), ...selectedItems.map(item => ({ url: item.url, alt: item.filename }))];
      updateNave(naveId, 'galeria', newGallery);
      toast.success(`${selectedItems.length} imagen(es) agregada(s) a galería`);
    } else if (type === 'video') {
      updateNave(naveId, 'video', items.url);
      toast.success('Video de nave seleccionado');
    }
  };

  const openCloudinaryFor = (naveId, type) => {
    setCloudinaryModal({ open: true, naveId, type });
  };

  const removeNave = (id) => {
    if (!confirm('¿Eliminar esta nave?')) return;
    setFormData(prev => ({ ...prev, naves: prev.naves.filter(n => n.id !== id) }));
  };

  const handleNaveImageUpload = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingNaveImg(id);
    try {
      const result = await uploadMedia(file);
      updateNave(id, 'imagen', result.url);
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingNaveImg(null);
    }
  };

  const handleNaveGalleryUpload = async (id, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    try {
      const results = await Promise.all(files.map(f => uploadMedia(f)));
      const nave = formData.naves.find(n => n.id === id);
      const newGallery = [...(nave?.galeria || []), ...results.map((r, i) => ({ url: r.url, alt: files[i].name }))];
      updateNave(id, 'galeria', newGallery);
      toast.success(`${results.length} imagen(es) agregada(s) a galería`);
    } catch (error) {
      toast.error('Error al subir imágenes');
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
        <strong>Naves/Unidades:</strong> Cada nave tiene su propia ficha con imagen, estado, especificaciones y galería.
      </div>

      <div className="space-y-3">
        {formData.naves?.map((nave) => (
          <div key={nave.id} className="border border-slate-200 rounded-lg overflow-hidden">
            {/* Header */}
            <div 
              className="flex items-center gap-3 p-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
              onClick={() => setExpandedNave(expandedNave === nave.id ? null : nave.id)}
            >
              <GripVertical size={16} className="text-slate-400" />
              <div className="w-16 h-10 rounded bg-slate-200 overflow-hidden flex-shrink-0">
                {nave.imagen && <img src={nave.imagen} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm">{nave.nombre}</div>
                <div className="text-xs text-slate-500">{nave.estadoCustom || nave.estado} {nave.superficie && `• ${nave.superficie}`}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeNave(nave.id); }}>
                <Trash2 size={16} />
              </Button>
              {expandedNave === nave.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>

            {/* Content */}
            {expandedNave === nave.id && (
              <div className="p-4 space-y-4 border-t">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Nombre de la Nave</Label>
                    <Input value={nave.nombre} onChange={(e) => updateNave(nave.id, 'nombre', e.target.value)} />
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Select value={nave.estado} onValueChange={(v) => updateNave(nave.id, 'estado', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Disponible">Disponible</SelectItem>
                        <SelectItem value="Ocupada">Ocupada</SelectItem>
                        <SelectItem value="En Renovación">En Renovación</SelectItem>
                        <SelectItem value="Personalizado">Personalizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {nave.estado === 'Personalizado' && (
                  <div>
                    <Label>Estado Personalizado</Label>
                    <Input value={nave.estadoCustom} onChange={(e) => updateNave(nave.id, 'estadoCustom', e.target.value)} placeholder="Ej: Reservada, En Negociación" />
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Superficie</Label>
                    <Input value={nave.superficie} onChange={(e) => updateNave(nave.id, 'superficie', e.target.value)} placeholder="1.800 m²" />
                  </div>
                  <div>
                    <Label>Altura</Label>
                    <Input value={nave.altura} onChange={(e) => updateNave(nave.id, 'altura', e.target.value)} placeholder="8m" />
                  </div>
                </div>

                <div>
                  <Label>Descripción</Label>
                  <Textarea value={nave.descripcion} onChange={(e) => updateNave(nave.id, 'descripcion', e.target.value)} rows={2} />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label>Tipo de Techo</Label>
                    <Input value={nave.techo} onChange={(e) => updateNave(nave.id, 'techo', e.target.value)} placeholder="Chapa curva" />
                  </div>
                  <div>
                    <Label>Instalaciones</Label>
                    <Input value={nave.instalaciones} onChange={(e) => updateNave(nave.id, 'instalaciones', e.target.value)} placeholder="Eléctrica, agua, gas" />
                  </div>
                  <div>
                    <Label>Accesos</Label>
                    <Input value={nave.accesos} onChange={(e) => updateNave(nave.id, 'accesos', e.target.value)} placeholder="Portón vehicular" />
                  </div>
                </div>

                {/* Imagen principal */}
                <div>
                  <Label>Imagen Principal de la Nave</Label>
                  <div className="flex gap-2 mt-2">
                    <input type="file" accept="image/*" onChange={(e) => handleNaveImageUpload(nave.id, e)} className="flex-1 block text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100" />
                    <Button type="button" variant="outline" onClick={() => openCloudinaryFor(nave.id, 'image')} className="whitespace-nowrap">
                      <FolderOpen className="h-4 w-4 mr-2" /> Cloudinary
                    </Button>
                  </div>
                  {uploadingNaveImg === nave.id && <p className="text-xs text-slate-500 mt-1">Subiendo...</p>}
                  {nave.imagen && <img src={nave.imagen} alt="" className="mt-2 w-40 h-24 object-cover rounded-lg" />}
                </div>

                {/* Galería */}
                <div>
                  <Label>Galería de la Nave</Label>
                  <div className="flex gap-2 mt-2">
                    <input type="file" accept="image/*" multiple onChange={(e) => handleNaveGalleryUpload(nave.id, e)} className="flex-1 block text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100" />
                    <Button type="button" variant="outline" onClick={() => openCloudinaryFor(nave.id, 'gallery')} className="whitespace-nowrap">
                      <FolderOpen className="h-4 w-4 mr-2" /> Cloudinary
                    </Button>
                  </div>
                  {nave.galeria?.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {nave.galeria.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img.url} alt="" className="w-20 h-14 object-cover rounded" />
                          <button 
                            onClick={() => updateNave(nave.id, 'galeria', nave.galeria.filter((_, i) => i !== idx))}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Video */}
                <div>
                  <Label>Video de la Nave</Label>
                  <div className="mt-2 space-y-2">
                    <Input value={nave.video} onChange={(e) => updateNave(nave.id, 'video', e.target.value)} placeholder="URL del video o subí uno" />
                    <div className="flex gap-2">
                      <input 
                        type="file" 
                        accept="video/mp4,video/webm,video/mov" 
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const result = await uploadMedia(file);
                            updateNave(nave.id, 'video', result.url);
                            toast.success('Video subido');
                          } catch (error) {
                            toast.error('Error al subir video');
                          }
                        }} 
                        className="flex-1 block text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" 
                      />
                      <Button type="button" variant="outline" onClick={() => openCloudinaryFor(nave.id, 'video')} className="whitespace-nowrap">
                        <FolderOpen className="h-4 w-4 mr-2" /> Cloudinary
                      </Button>
                    </div>
                    {nave.video && (
                      <video src={nave.video} className="w-full max-w-sm rounded-lg mt-2" controls muted />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={addNave}>
        <Plus size={16} className="mr-2" /> Agregar Nave
      </Button>

      {/* Cloudinary Library Modal for Naves */}
      <CloudinaryLibrary 
        open={cloudinaryModal.open}
        onClose={() => setCloudinaryModal({ open: false, naveId: null, type: 'image' })}
        onSelect={handleCloudinarySelect}
        multiple={cloudinaryModal.type === 'gallery'}
        resourceType={cloudinaryModal.type === 'video' ? 'video' : 'image'}
      />
    </div>
  );
};

// ============== TAB: ÁREAS COMUNES ==============
const AreasTab = ({ formData, setFormData }) => {
  const [uploading, setUploading] = useState(false);

  const handleAreaUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const results = await Promise.all(files.map(f => uploadMedia(f)));
      const newAreas = results.map((r, i) => ({ imagen: r.url, label: files[i].name.replace(/\.[^/.]+$/, '') }));
      setFormData(prev => ({ ...prev, areasComunes: [...(prev.areasComunes || []), ...newAreas] }));
      toast.success(`${results.length} área(s) agregada(s)`);
    } catch (error) {
      toast.error('Error al subir imágenes');
    } finally {
      setUploading(false);
    }
  };

  const updateArea = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      areasComunes: prev.areasComunes.map((a, i) => i === index ? { ...a, [field]: value } : a),
    }));
  };

  const removeArea = (index) => {
    setFormData(prev => ({ ...prev, areasComunes: prev.areasComunes.filter((_, i) => i !== index) }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadMedia(file);
      setFormData(prev => ({ ...prev, areasComunesVideo: result.url }));
      toast.success('Video subido');
    } catch (error) {
      toast.error('Error al subir video');
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
        <strong>Áreas Comunes:</strong> Fotos de espacios compartidos (estacionamiento, seguridad, áreas verdes, etc).
      </div>

      <div>
        <Label>Subir Imágenes de Áreas Comunes</Label>
        <input type="file" accept="image/*" multiple onChange={handleAreaUpload} className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 mt-2" />
        {uploading && <p className="text-xs text-slate-500 mt-1">Subiendo...</p>}
      </div>

      {formData.areasComunes?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {formData.areasComunes.map((area, idx) => (
            <div key={idx} className="relative group">
              <img src={area.imagen} alt={area.label} className="w-full aspect-[3/2] object-cover rounded-lg" />
              <Input 
                value={area.label} 
                onChange={(e) => updateArea(idx, 'label', e.target.value)} 
                className="mt-1 text-xs h-8" 
                placeholder="Etiqueta"
              />
              <button 
                onClick={() => removeArea(idx)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-sm opacity-0 group-hover:opacity-100"
              >×</button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t pt-4 mt-4">
        <Label>Video de Áreas Comunes</Label>
        <div className="mt-2 space-y-2">
          <Input value={formData.areasComunesVideo || ''} onChange={(e) => setFormData({ ...formData, areasComunesVideo: e.target.value })} placeholder="URL del video" />
          <input type="file" accept="video/mp4,video/webm" onChange={handleVideoUpload} className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100" />
          {formData.areasComunesVideo && (
            <video src={formData.areasComunesVideo} className="w-full max-w-md rounded-lg" controls muted />
          )}
        </div>
      </div>
    </div>
  );
};

// ============== TAB: UBICACIÓN ==============
const UbicacionTab = ({ formData, setFormData }) => {
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
        <strong>Ubicación:</strong> Mapa de Google Maps e información de cómo llegar.
      </div>

      <div>
        <Label>URL de Google Maps Embed</Label>
        <Input value={formData.mapEmbedUrl || ''} onChange={(e) => setFormData({ ...formData, mapEmbedUrl: e.target.value })} placeholder="https://www.google.com/maps/embed?pb=..." />
        <p className="text-xs text-slate-500 mt-1">Obtené la URL desde Google Maps → Compartir → Insertar un mapa</p>
      </div>

      <div>
        <Label>Dirección</Label>
        <Textarea 
          value={formData.ubicacionInfo?.direccion || ''} 
          onChange={(e) => setFormData({ ...formData, ubicacionInfo: { ...formData.ubicacionInfo, direccion: e.target.value } })} 
          rows={2}
          placeholder="Calle Ejemplo 1234, Martelli&#10;Buenos Aires, Argentina"
        />
      </div>

      <div>
        <Label>Accesos</Label>
        <Textarea 
          value={formData.ubicacionInfo?.accesos || ''} 
          onChange={(e) => setFormData({ ...formData, ubicacionInfo: { ...formData.ubicacionInfo, accesos: e.target.value } })} 
          rows={2}
          placeholder="A 5 minutos de Av. General Paz&#10;Acceso directo por Ruta 202"
        />
      </div>

      <div>
        <Label>Transporte Público</Label>
        <Textarea 
          value={formData.ubicacionInfo?.transporte || ''} 
          onChange={(e) => setFormData({ ...formData, ubicacionInfo: { ...formData.ubicacionInfo, transporte: e.target.value } })} 
          rows={2}
          placeholder="Líneas de colectivo: 176, 203&#10;Estación tren: Martelli (Belgrano Norte)"
        />
      </div>
    </div>
  );
};
